/**
 * FAQ Load More Functionality
 * Handles both ungrouped and grouped FAQs with independent load more per group
 */
(function () {
	'use strict';

	// Wait for DOM to be ready
	document.addEventListener('DOMContentLoaded', function () {
		initFaqLoadMore();
	});

	function initFaqLoadMore() {
		// Get all FAQ blocks on the page
		const faqBlocks = document.querySelectorAll('.block-faq');

		faqBlocks.forEach(function (block) {
			setupLoadMore(block);
		});
	}

	function setupLoadMore(block) {
		const accordion = block.querySelector('.accordion');
		if (!accordion) return;

		const itemsPerPage = window.faqConfig?.itemsPerPage || 6;
		const hasGroups = accordion.querySelector('h2.faq-group-header') !== null;

		if (hasGroups) {
			setupGroupedLoadMore(accordion, itemsPerPage);
		} else {
			setupSimpleLoadMore(accordion, itemsPerPage);
		}
	}

	/**
	 * Setup load more for ungrouped FAQs
	 */
	function setupSimpleLoadMore(accordion, itemsPerPage) {
		const allCards = Array.from(accordion.querySelectorAll('.card'));

		// If there are 6 or fewer items, no need for load more
		if (allCards.length <= itemsPerPage) return;

		let currentlyVisible = itemsPerPage;
		let buttonWrapper = null;
		let loadMoreBtn = null;

		// Initially hide items beyond the first 6
		allCards.forEach(function (card, index) {
			if (index >= itemsPerPage) {
				card.style.display = 'none';
				card.classList.add('faq-hidden');
			}
		});

		// Create the load more button wrapper
		buttonWrapper = document.createElement('div');
		buttonWrapper.className = 'faq-load-more-wrapper';

		loadMoreBtn = document.createElement('button');
		loadMoreBtn.className = 'faq-load-more-btn btn btn-primary';
		loadMoreBtn.textContent = window.faqConfig?.loadMoreText || 'Load more';
		loadMoreBtn.setAttribute('aria-expanded', 'false');
		loadMoreBtn.setAttribute('aria-controls', accordion.id);

		buttonWrapper.appendChild(loadMoreBtn);

		// Insert button after the last visible card (inside accordion)
		const lastVisibleCard = allCards[itemsPerPage - 1];
		if (lastVisibleCard.nextElementSibling) {
			accordion.insertBefore(buttonWrapper, lastVisibleCard.nextElementSibling);
		} else {
			accordion.appendChild(buttonWrapper);
		}

		// Add click event
		loadMoreBtn.addEventListener('click', handleLoadMore);

		function handleLoadMore() {
			const hiddenCards = allCards.filter((card) => card.classList.contains('faq-hidden'));
			const cardsToShow = hiddenCards.slice(0, itemsPerPage);

			if (cardsToShow.length > 0) {
				// Show the next batch of items
				cardsToShow.forEach(function (card) {
					card.style.display = '';
					card.classList.remove('faq-hidden');
					card.classList.add('faq-fade-in');
				});

				currentlyVisible += cardsToShow.length;

				// Move button after the last newly visible card
				moveButtonAfterLastVisible();

				// Update button text and state
				updateLoadMoreButton();
			}
		}

		function moveButtonAfterLastVisible() {
			const visibleCards = allCards.filter((card) => !card.classList.contains('faq-hidden'));
			const lastVisible = visibleCards[visibleCards.length - 1];

			if (lastVisible) {
				if (
					lastVisible.nextElementSibling &&
					lastVisible.nextElementSibling !== buttonWrapper
				) {
					accordion.insertBefore(buttonWrapper, lastVisible.nextElementSibling);
				} else if (!lastVisible.nextElementSibling) {
					accordion.appendChild(buttonWrapper);
				}
			}
		}

		function updateLoadMoreButton() {
			const hiddenCards = allCards.filter((card) => card.classList.contains('faq-hidden'));

			if (hiddenCards.length === 0) {
				// All items are visible, change to "Show less" button
				loadMoreBtn.textContent = window.faqConfig?.loadLessText || 'Show less';
				loadMoreBtn.classList.add('show-less-mode');
				loadMoreBtn.removeEventListener('click', handleLoadMore);
				loadMoreBtn.addEventListener('click', handleShowLess);
				loadMoreBtn.setAttribute('aria-expanded', 'true');
			} else {
				// Update button text with remaining count
				const remaining = hiddenCards.length;
				loadMoreBtn.textContent = `${window.faqConfig?.loadMoreText || 'Load more'} (${remaining})`;
			}
		}

		function handleShowLess() {
			// Hide all items except the first batch
			allCards.forEach(function (card, index) {
				if (index >= itemsPerPage) {
					card.style.display = 'none';
					card.classList.add('faq-hidden');
					card.classList.remove('faq-fade-in');
				}
			});

			currentlyVisible = itemsPerPage;

			// Move button back to after the 6th item
			const sixthCard = allCards[itemsPerPage - 1];
			if (sixthCard.nextElementSibling) {
				accordion.insertBefore(buttonWrapper, sixthCard.nextElementSibling);
			}

			// Reset button
			loadMoreBtn.textContent = window.faqConfig?.loadMoreText || 'Load more';
			loadMoreBtn.classList.remove('show-less-mode');
			loadMoreBtn.removeEventListener('click', handleShowLess);
			loadMoreBtn.addEventListener('click', handleLoadMore);
			loadMoreBtn.setAttribute('aria-expanded', 'false');

			// Update button with count
			updateLoadMoreButton();
		}

		// Initial button update
		updateLoadMoreButton();
	}

	/**
	 * Setup load more for grouped FAQs - each group gets its own load more
	 */
	function setupGroupedLoadMore(accordion, itemsPerPage) {
		const groups = accordion.querySelectorAll('h2.faq-group-header');
		if (groups.length === 0) return;

		const groupData = [];

		// Process each group
		groups.forEach(function (groupHeader, groupIndex) {
			const groupCards = [];
			let nextSibling = groupHeader.nextElementSibling;

			// Collect all cards for this group
			while (nextSibling && !nextSibling.matches('h2')) {
				if (nextSibling.classList.contains('card')) {
					groupCards.push(nextSibling);
				}
				nextSibling = nextSibling.nextElementSibling;
			}

			// Only process groups with more than 6 items
			if (groupCards.length > itemsPerPage) {
				const group = {
					header: groupHeader,
					cards: groupCards,
					currentlyVisible: itemsPerPage,
					index: groupIndex,
					buttonWrapper: null,
				};

				// Hide items beyond the first 6 in this group
				groupCards.forEach(function (card, index) {
					if (index >= itemsPerPage) {
						card.style.display = 'none';
						card.classList.add('faq-hidden');
						card.setAttribute('data-group', groupIndex);
					}
				});

				// Create load more button for this group
				const buttonWrapper = createGroupLoadMoreButton(group, itemsPerPage);
				group.buttonWrapper = buttonWrapper;

				// Insert button after the last visible card of this group
				const lastVisibleCard = groupCards[itemsPerPage - 1];
				if (lastVisibleCard && lastVisibleCard.nextElementSibling) {
					lastVisibleCard.parentNode.insertBefore(
						buttonWrapper,
						lastVisibleCard.nextElementSibling
					);
				} else if (lastVisibleCard) {
					lastVisibleCard.parentNode.appendChild(buttonWrapper);
				}

				groupData.push(group);
			}
		});

		// Setup event handlers for each group's button
		groupData.forEach(function (group) {
			setupGroupEventHandlers(group, itemsPerPage);
		});
	}

	/**
	 * Create load more button for a specific group
	 */
	function createGroupLoadMoreButton(group, itemsPerPage) {
		const buttonWrapper = document.createElement('div');
		buttonWrapper.className = 'faq-load-more-wrapper faq-group-load-more';
		buttonWrapper.setAttribute('data-group', group.index);

		const loadMoreBtn = document.createElement('button');
		loadMoreBtn.className = 'faq-load-more-btn btn btn-primary';
		loadMoreBtn.setAttribute('aria-expanded', 'false');
		loadMoreBtn.setAttribute('data-group', group.index);

		// Calculate hidden items for this group
		const hiddenCount = group.cards.length - itemsPerPage;
		loadMoreBtn.textContent = `${window.faqConfig?.loadMoreText || 'Load more'} (${hiddenCount})`;

		buttonWrapper.appendChild(loadMoreBtn);
		return buttonWrapper;
	}

	/**
	 * Setup event handlers for a group's load more button
	 */
	function setupGroupEventHandlers(group, itemsPerPage) {
		const buttonWrapper = group.buttonWrapper;
		if (!buttonWrapper) return;

		const loadMoreBtn = buttonWrapper.querySelector('.faq-load-more-btn');
		if (!loadMoreBtn) return;

		loadMoreBtn.addEventListener('click', function () {
			handleGroupLoadMore(group, itemsPerPage, loadMoreBtn, buttonWrapper);
		});
	}

	/**
	 * Move button after the last visible card in a group
	 */
	function moveGroupButtonAfterLastVisible(group, buttonWrapper) {
		const visibleCards = group.cards.filter((card) => !card.classList.contains('faq-hidden'));
		const lastVisible = visibleCards[visibleCards.length - 1];

		if (lastVisible) {
			// Insert after the last visible card
			if (
				lastVisible.nextElementSibling &&
				lastVisible.nextElementSibling !== buttonWrapper
			) {
				lastVisible.parentNode.insertBefore(buttonWrapper, lastVisible.nextElementSibling);
			} else if (!lastVisible.nextElementSibling) {
				// If last visible is the last element, append the button
				lastVisible.parentNode.appendChild(buttonWrapper);
			}
		}
	}

	/**
	 * Handle load more for a specific group
	 */
	function handleGroupLoadMore(group, itemsPerPage, button, buttonWrapper) {
		const hiddenCards = group.cards.filter((card) => card.classList.contains('faq-hidden'));

		if (button.classList.contains('show-less-mode')) {
			// Show less mode - hide all except first batch
			group.cards.forEach(function (card, index) {
				if (index >= itemsPerPage) {
					card.style.display = 'none';
					card.classList.add('faq-hidden');
					card.classList.remove('faq-fade-in');
				}
			});

			group.currentlyVisible = itemsPerPage;

			// Move button back to after the 6th card
			const sixthCard = group.cards[itemsPerPage - 1];
			if (sixthCard && sixthCard.nextElementSibling) {
				sixthCard.parentNode.insertBefore(buttonWrapper, sixthCard.nextElementSibling);
			}

			// Update button
			const hiddenCount = group.cards.length - itemsPerPage;
			button.textContent = `${window.faqConfig?.loadMoreText || 'Load more'} (${hiddenCount})`;
			button.classList.remove('show-less-mode');
			button.setAttribute('aria-expanded', 'false');
		} else {
			// Load more mode
			const cardsToShow = hiddenCards.slice(0, itemsPerPage);

			if (cardsToShow.length > 0) {
				// Show the next batch of items
				cardsToShow.forEach(function (card) {
					card.style.display = '';
					card.classList.remove('faq-hidden');
					card.classList.add('faq-fade-in');
				});

				group.currentlyVisible += cardsToShow.length;

				// Move button after the last newly visible card
				moveGroupButtonAfterLastVisible(group, buttonWrapper);

				// Update button
				const remainingHidden = group.cards.filter((card) =>
					card.classList.contains('faq-hidden')
				).length;

				if (remainingHidden === 0) {
					// All items visible - switch to show less
					button.textContent = window.faqConfig?.loadLessText || 'Show less';
					button.classList.add('show-less-mode');
					button.setAttribute('aria-expanded', 'true');
				} else {
					// Update count
					button.textContent = `${window.faqConfig?.loadMoreText || 'Load more'} (${remainingHidden})`;
				}
			}
		}
	}
})();
